import React, { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, Eye, EyeOff, Github, ExternalLink, BookOpen, Zap } from 'lucide-react'

import Step1Discover from './components/Step1Discover'
import Step2VerifyAccess from './components/Step2VerifyAccess'
import Step3LogActivity from './components/Step3LogActivity'
import Step4Compliance from './components/Step4Compliance'
import DebugPanel from './components/DebugPanel'
import { mockServer, DSAPRequest, DSAPResponse, Scenario } from './mock/dsap-mock'

interface PlaygroundState {
  currentStep: number
  debugMode: boolean
  selectedScenario: Scenario | null
  agentCard: any
  accessDecision: any
  auditData: any
  complianceData: any
  requestHistory: Array<{ request: DSAPRequest; response: DSAPResponse; timestamp: string }>
}

export default function App() {
  const [state, setState] = useState<PlaygroundState>({
    currentStep: 1,
    debugMode: false,
    selectedScenario: null,
    agentCard: null,
    accessDecision: null,
    auditData: null,
    complianceData: null,
    requestHistory: []
  })

  const addToHistory = useCallback((request: DSAPRequest, response: DSAPResponse) => {
    setState(prev => ({
      ...prev,
      requestHistory: [
        ...prev.requestHistory,
        {
          request,
          response,
          timestamp: new Date().toISOString()
        }
      ]
    }))
  }, [])

  const handleStepComplete = useCallback((stepData: any) => {
    setState(prev => ({
      ...prev,
      ...stepData,
      currentStep: prev.currentStep + 1
    }))
  }, [])

  const handleScenarioSelect = useCallback((scenario: Scenario) => {
    setState(prev => ({
      ...prev,
      selectedScenario: scenario,
      currentStep: 1,
      agentCard: null,
      accessDecision: null,
      auditData: null,
      complianceData: null,
      requestHistory: []
    }))
    mockServer.reset()
  }, [])

  const handleReset = useCallback(() => {
    setState({
      currentStep: 1,
      debugMode: state.debugMode,
      selectedScenario: null,
      agentCard: null,
      accessDecision: null,
      auditData: null,
      complianceData: null,
      requestHistory: []
    })
    mockServer.reset()
  }, [state.debugMode])

  const steps = [
    { id: 1, title: 'Discover Server', description: 'Learn about DSAP capabilities' },
    { id: 2, title: 'Request Access', description: 'Submit access verification request' },
    { id: 3, title: 'Log Activity', description: 'Record data operation in audit trail' },
    { id: 4, title: 'Check Compliance', description: 'Generate compliance report' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <Shield className="h-8 w-8 text-dsap-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    DSAP Interactive Playground
                  </h1>
                  <p className="text-sm text-gray-600">
                    Experience Data Security Access Protocol in action
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Debug Mode Toggle */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Debug Mode</span>
                <button
                  onClick={() => setState(prev => ({ ...prev, debugMode: !prev.debugMode }))}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-dsap-500 focus:ring-offset-2 ${
                    state.debugMode ? 'bg-dsap-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                      state.debugMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {state.debugMode ? (
                  <Eye className="h-4 w-4 text-dsap-600" />
                ) : (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                )}
              </div>

              <div className="flex items-center space-x-2">
                <a
                  href="https://github.com/Threatwrix/DSAP"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-dsap-600 transition-colors"
                >
                  <Github className="h-4 w-4 mr-1" />
                  GitHub
                </a>
                <a
                  href="https://github.com/Threatwrix/DSAP/tree/main/protocol-spec"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-dsap-600 transition-colors"
                >
                  <BookOpen className="h-4 w-4 mr-1" />
                  Spec
                </a>
              </div>

              <button
                onClick={handleReset}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Zap className="h-4 w-4 mr-2" />
                Reset Demo
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  DSAP Protocol Flow
                </h2>
                <span className="text-sm text-gray-500">
                  Step {state.currentStep} of {steps.length}
                </span>
              </div>
              
              <div className="flex items-center space-x-4">
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <div className="flex items-center space-x-3">
                      <div
                        className={`step-indicator ${
                          step.id < state.currentStep
                            ? 'step-indicator-completed'
                            : step.id === state.currentStep
                            ? 'step-indicator-active'
                            : 'step-indicator-upcoming'
                        }`}
                      >
                        {step.id < state.currentStep ? '✓' : step.id}
                      </div>
                      <div className="hidden md:block">
                        <div className="text-sm font-medium text-gray-900">
                          {step.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {step.description}
                        </div>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 ${
                          step.id < state.currentStep ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={state.currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {state.currentStep === 1 && (
                  <Step1Discover
                    onComplete={handleStepComplete}
                    onScenarioSelect={handleScenarioSelect}
                    selectedScenario={state.selectedScenario}
                    mockServer={mockServer}
                    addToHistory={addToHistory}
                  />
                )}
                {state.currentStep === 2 && (
                  <Step2VerifyAccess
                    onComplete={handleStepComplete}
                    selectedScenario={state.selectedScenario}
                    agentCard={state.agentCard}
                    mockServer={mockServer}
                    addToHistory={addToHistory}
                  />
                )}
                {state.currentStep === 3 && (
                  <Step3LogActivity
                    onComplete={handleStepComplete}
                    selectedScenario={state.selectedScenario}
                    accessDecision={state.accessDecision}
                    mockServer={mockServer}
                    addToHistory={addToHistory}
                  />
                )}
                {state.currentStep === 4 && (
                  <Step4Compliance
                    onComplete={handleStepComplete}
                    selectedScenario={state.selectedScenario}
                    auditData={state.auditData}
                    mockServer={mockServer}
                    addToHistory={addToHistory}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Debug Panel Sidebar */}
          {state.debugMode && (
            <div className="lg:col-span-1">
              <DebugPanel requestHistory={state.requestHistory} />
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              This playground is a simulation running entirely in your browser.{' '}
              <a
                href="https://github.com/Threatwrix/DSAP/tree/main/protocol-spec"
                target="_blank"
                rel="noopener noreferrer"
                className="text-dsap-600 hover:text-dsap-700 font-medium"
              >
                View the full DSAP specification
                <ExternalLink className="inline h-3 w-3 ml-1" />
              </a>
            </p>
            <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-500">
              <span>Built for the agent era</span>
              <span>•</span>
              <span>Secure by design</span>
              <span>•</span>
              <span>Open by default</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}